import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { Container, Graphics } from "pixi.js";
import { FeatureReelSymbol } from "./FeatureReelSymbol";
import { RandomUtil } from "@lib/titan2/dist/util/random/RandomUtil";
import gsap from "gsap";
import { MotionBlurFilter } from "pixi-filters";
import { TweenConstants } from "@lib/titan2-slingo/dist/game/view/abstract/constant/TweenConstants";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
const reelSet = [
    "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLANK", "BLUE", "GREEN", "RED", "PURPLE", "GOLD"
];
export class FeatureReel extends AbstractGameView {
    constructor(factory, landscapeProperties, portraitProperties) {
        super(factory, landscapeProperties, portraitProperties);
    }
    create() {
        super.create();
        this.maskedSpinContainer = this.addChild(new Container());
        this.reelSpinSymbolsContainer = this.maskedSpinContainer.addChild(new Container());
        this.reelSymbolLandedContainer = this.addChild(new Container());
        const randomisedSet = reelSet.slice();
        RandomUtil.shuffle(randomisedSet);
        this.spinSymbols = randomisedSet.map(id => {
            const symbol = this.reelSpinSymbolsContainer.addChild(new FeatureReelSymbol(this.factory, this.landscapeProperties.reelSymbolProperties, this.portraitProperties.reelSymbolProperties));
            symbol.setId(id);
            return symbol;
        });
        this.reelSymbolLanded = this.reelSymbolLandedContainer.addChild(new FeatureReelSymbol(this.factory, this.landscapeProperties.reelSymbolProperties, this.portraitProperties.reelSymbolProperties));
        this.reelSpinSymbolsContainer.visible = false;
        this.reelSymbolLandedContainer.visible = false;
        this.reelSpinSymbolsContainer.filters = [new MotionBlurFilter(TweenConstants.MOTION_BLUR)];
        this.maskGraphic = new Graphics();
        this.maskGraphic.name = "mask";
        this.maskGraphic.beginFill(0xffffff, 1);
        this.maskGraphic.drawRect(0, 0, 170, 170);
        this.maskedSpinContainer.addChild(this.maskGraphic);
        this.maskedSpinContainer.mask = this.maskGraphic;
        this.lock = TitanSpine.createSpine(this, "lock", this.landscapeProperties.lock, this.portraitProperties.lock);
        this.lock.visible = false;
        this.payout = TitanText.createField(this, this.landscapeProperties.value, this.portraitProperties.value);
        this.payout.setAnchorY(0.5);
        this.payout.alpha = 0;
        this.multipliedPayout = TitanText.createField(this, this.landscapeProperties.multipliedValue, this.portraitProperties.multipliedValue);
        this.multipliedPayout.setAnchorY(0.5);
        this.multipliedPayout.alpha = 0;
    }
    setReelIndex(reelIndex) {
        this.reelSymbolLanded.setReelIndex(reelIndex);
    }
    spin() {
        this.hidePayout();
        this.reelSymbolLandedContainer.visible = false;
        this.reelSpinSymbolsContainer.visible = true;
        this.spinSymbols.forEach((child, i) => child.y = -child.getHeight() * (i + 1));
        const symbolHeight = this.spinSymbols[0].getHeight();
        const totalHeight = symbolHeight * this.spinSymbols.length;
        const startingPositions = this.reelSpinSymbolsContainer.children.map(child => child.y);
        const offset = { y: 0 };
        this.reelSpinAnimation = gsap.to(offset, {
            y: totalHeight + symbolHeight,
            duration: 1,
            repeat: -1,
            ease: "none",
            onUpdate: () => {
                this.spinSymbols.forEach((elem, i) => {
                    elem.y = (startingPositions[i] + offset.y) % (totalHeight + symbolHeight);
                    if (elem.y > totalHeight + symbolHeight) {
                        elem.y -= totalHeight;
                    }
                });
            },
        });
    }
    stop(id, payout) {
        this.reelSpinSymbolsContainer.visible = false;
        this.reelSpinAnimation.kill();
        this.reelSymbolLandedContainer.visible = true;
        this.reelSymbolLanded.setId(id);
        gsap.fromTo(this.reelSymbolLanded, {
            y: 0
        }, {
            y: 10,
            repeat: 1,
            yoyo: true,
            duration: 0.125
        });
    }
    revealPayout(payout) {
        if (payout <= 0) {
            return;
        }
        this.payout.setText(this.formatCurrency(payout), true);
        this.payout.alpha = 1;
        gsap.to(this.payout.scale, {
            x: 1.2,
            y: 1.2,
            yoyo: true,
            repeat: 1,
            duration: 0.25
        });
    }
    applyPayoutMultiplier(unmultipiedPayout, multipliedPayout) {
        const valueTarget = { value: unmultipiedPayout };
        this.multipliedPayout.setText(this.formatCurrency(multipliedPayout), true);
        const duration = 0.5;
        gsap.fromTo(this.payout, { alpha: 1 }, {
            alpha: 0,
            duration: duration / 4,
            delay: (duration / 4) * 3
        });
        gsap.fromTo(this.multipliedPayout, { alpha: 0 }, {
            alpha: 1,
            duration: duration,
        });
        gsap.to(this.payout.getPixiText(), {
            pixi: {
                scale: 1.2
            },
            duration: duration / 2,
            yoyo: true,
            repeat: 1,
            ease: "circ.in",
            yoyoEase: "circ.out"
        });
        gsap.to(this.multipliedPayout.getPixiText(), {
            pixi: {
                scale: 1.2
            },
            duration: duration / 2,
            yoyo: true,
            repeat: 1,
            ease: "circ.in",
            yoyoEase: "circ.out"
        });
        gsap.to(valueTarget, {
            value: multipliedPayout,
            duration,
            onUpdate: () => {
                this.payout.setText(this.formatCurrency(valueTarget.value), true);
                this.multipliedPayout.setText(this.formatCurrency(valueTarget.value), true);
            },
            onComplete: () => {
                this.payout.setText(this.formatCurrency(multipliedPayout), true);
                this.multipliedPayout.setText(this.formatCurrency(multipliedPayout), true);
            }
        });
    }
    getGlobalPayoutPosition() {
        return this.toGlobal(this.payout.position);
    }
    getReelCenterPosition() {
        const centerPosition = this.reelSymbolLanded.position.clone();
        centerPosition.x += this.reelSymbolLanded.getWidth() / 2;
        centerPosition.y += this.reelSymbolLanded.getHeight() / 2;
        return this.reelSymbolLanded.parent.toGlobal(centerPosition);
    }
    hideReelSymbol() {
        this.reelSymbolLandedContainer.visible = false;
    }
    getReelSymbol() {
        return this.reelSymbolLandedContainer;
    }
    getPayoutTitanText() {
        return this.multipliedPayout.alpha > 0 ? this.multipliedPayout : this.payout;
    }
    hidePayout() {
        this.payout.alpha = 0;
        this.multipliedPayout.alpha = 0;
    }
    clear() {
        this.reelSpinSymbolsContainer.visible = false;
        this.reelSpinAnimation?.kill();
        this.reelSymbolLandedContainer.visible = false;
    }
    enableLock() {
        if (this.lock.visible) {
            return;
        }
        SpineUtil.play(this.lock, "lockIn");
        this.lock.getSpine().update(0.01);
        this.lock.visible = true;
    }
    clearLock() {
        if (this.lock.visible) {
            SpineUtil.play(this.lock, "lockOut", () => {
                this.lock.visible = false;
            });
        }
    }
    getWidth() {
        return this.uiProperties.reelSymbolProperties.size.x;
    }
    getHeight() {
        return this.uiProperties.reelSymbolProperties.size.y;
    }
}
//# sourceMappingURL=FeatureReel.js.map